import styled from "@emotion/styled";
import { Box, Button, Modal, CircularProgress } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  getWithQueryApiServices,
  postWithQueryApiServices,
  putWithQueryApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { invoiceFormInitVal } from "../../../constants";
import { StyledTypography } from "../../../styles/commonStyles";
import { errorToast, successToast } from "../../../utils/helper";
import { FullFeaturedCrudGrid } from "../../shared/CommonTable";
import { CustomDatePicker } from "../../shared/CustomDatePicker";
import { CustomSelectField } from "../../shared/CustomSelectField";
import { CustomTextField } from "../../shared/CustomTextField";
import PaymentForm from "../../shared/PaymentForm";

const StyledInput = styled("input")(({ theme }) => ({
  backgroundColor: "white",
  borderRadius: 4,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "#bdbdbd",
  height: 26,
  margin: "0 4px 0 12px",
}));

const CriteriaOptBox = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: "#deedf5",
  boxSizing: "border-box",
  padding: "10px 0",
  margin: "12px 0",
  borderRadius: 20,
  paddingLeft: 6,
}));

const formatUsd = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  return `$${n.toLocaleString()}`;
};

const columns = (editable) => [
  { field: "item", headerName: "Item / Service", flex: 1, editable },
  {
    field: "quantity",
    headerName: "Quantity",
    align: "left",
    headerAlign: "left",
    editable,
    flex: 0.5,
    type: "number",
  },
  {
    field: "price",
    headerName: "Price",
    align: "left",
    headerAlign: "left",
    editable,
    flex: 0.5,
    type: "number",
    valueFormatter: (value) => formatUsd(value),
  },
  {
    field: "recurring",
    headerName: "Recurring",
    align: "left",
    headerAlign: "left",
    editable,
    flex: 0.5,
    type: "boolean",
    required: false,
  },
];
export const InvoiceForm = ({
  categoryListRefetch,
  companyListRefetch,
  companyForm,
  setCompanyForm,
  categoryData,
  setCategoryData,
  addNewCategory,
  setAddNewCategory,
  setSelectedCategories,
  permission,
  readOnly = false,
}) => {
  const [categoryValues, setCategoryValues] = useState(invoiceFormInitVal);
  const [criteriaValues, setCriteriaValues] = useState([]);

  const { currentCompany, tenantString, fullCompanyList } = useOutletContext();
  const queryClient = useQueryClient();
  const [openPayment, setOpenPayment] = useState(false);

  const formHandleChange = (name, value) => {
    setCategoryValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Define required fields for the custom form
  const requiredFields = [
    { name: "companyID", label: "Invoiced to Company" },
    { name: "tax", label: "Tax %" },
    { name: "invoicedate", label: "Invoice Date" },
  ];

  const sumbitCompanyForm = () => {
    const missingFields = requiredFields
      .filter(field => categoryValues[field.name] === undefined || categoryValues[field.name] === "")
      .map(field => field.label);

    if (missingFields.length > 0) {
      return errorToast(`Please fill the following fields: ${missingFields.join(", ")}`);
    }
    if (criteriaValues.length < 1) {
      return errorToast(`Please add invoice item`);
    }


    if (
      categoryValues.invoicedate &&
      categoryValues.duedate &&
      moment(categoryValues.duedate).isBefore(moment(categoryValues.invoicedate), 'day')
    ) {
      return errorToast("Due date must be on or after invoice date");
    }

    if (categoryData?.invoiceID) {
      updateCategory.mutate();
    } else {
      createCategory.mutate();
    }
  };

  const createCategory = useMutation({
    mutationKey: ["createInvoiceform"],
    mutationFn: () =>
      postWithQueryApiServices(
        apiRoutes.GET_INVOICE_LIST,
        `selectedCompany=${currentCompany}${tenantString}`,
        { ...categoryValues, shippingcost: 0, invoiceitems: criteriaValues }
      ),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: ["invoiceLists"] });
      setAddNewCategory(false);
      categoryListRefetch();
      successToast("Invoice added Successfully");
      setCategoryValues(invoiceFormInitVal);
      setSelectedCategories([]);
      setCategoryData({});
    },
  });

  const updateCategory = useMutation({
    mutationKey: ["updateInvoiceform"],
    mutationFn: () =>
      putWithQueryApiServices(
        apiRoutes.GET_INVOICE_LIST,
        `selectedCompany=${currentCompany}${tenantString}`,
        { ...categoryValues, shippingcost: 0, invoiceitems: criteriaValues }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoiceLists"] });
      categoryListRefetch();
      setAddNewCategory(false);
      successToast("Invoice Updated Successfully");
      setCategoryValues(invoiceFormInitVal);
      setCategoryData({});
      setSelectedCategories([]);
    },
  });

  const isSaving = createCategory.isPending || updateCategory.isPending;

  const payInvoiceMutation = useMutation({
    mutationKey: ["payInvoiceFromForm"],
    mutationFn: ({ invoiceID, formData }) =>
      postWithQueryApiServices(
        `${apiRoutes.PAY_INVOICE}/${invoiceID}`,
        `selectedCompany=${categoryData?.companyID || currentCompany || ""}${tenantString || ""}`,
        formData
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoiceLists"] });
      queryClient.invalidateQueries({ queryKey: ["invoiceAlerts"] });
      categoryListRefetch();
      setOpenPayment(false);
      successToast("Invoice Paid Successfully");
    },
    onError: (err) => {
      errorToast(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Payment failed. Please try again."
      );
    },
  });


  const rowUpdateFun = (newRow) => {
    if (readOnly) return newRow;
    const cols = columns(!readOnly);
    const requiredColumns = cols.filter(col => col.required !== false);
    const columnNames = requiredColumns.map((obj) => obj.field);
    const missingFields = columnNames.filter((key) => newRow[key] === undefined || newRow[key] === "");
    if (missingFields.length > 0) {
      const missingFieldsMsg = missingFields
        .map((key) => {
          const col = cols.find((c) => c.field === key);
          return col && col.headerName ? col.headerName : key;
        })
        .join(", ");
      return errorToast(`Please fill the following fields: ${missingFieldsMsg}`);
    }else{
      if (newRow.isNew) {
        setCriteriaValues((prev) => [
          ...prev,
          { ...newRow, isNew: false, invoiceitemID: newRow.id },
        ]);
      } else {
        const updatedRow = [...criteriaValues].map((item) => {
          if (item.invoiceitemID === newRow.invoiceitemID) {
            return { ...newRow };
          } else {
            return item;
          }
        });
        setCriteriaValues(updatedRow);
      }
      return { ...newRow, isNew: false };
    }
  };


  useEffect(() => {
    if (categoryData?.invoiceID) {
      setCategoryValues({
        ...categoryData,
        duedate: categoryData.duedate ? new Date(categoryData.duedate) : "",
        invoicedate: categoryData.invoicedate
          ? new Date(categoryData.invoicedate)
          : "",
        shippingcost: 0,
      });

      if (categoryData?.invoiceitems?.length > 0) {
        setCriteriaValues(JSON.parse(categoryData?.invoiceitems));
      } else {
        setCriteriaValues([]);
      }
    } else {
      setCategoryValues(invoiceFormInitVal);
      setCriteriaValues([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryData?.invoiceID]);

  useEffect(() => {
    let amount = criteriaValues?.reduce((sum, obj) => {
      const q = parseInt(obj.quantity) || 0;
      const p = parseInt(obj.price) || 0;
      return sum + q * p;
    }, 0);
    let recurring_amount = criteriaValues?.reduce((sum, obj) => {
      if (obj.recurring) {
        const q = parseInt(obj.quantity) || 0;
        const p = parseInt(obj.price) || 0;
        return sum + q * p;
      } else {
        return sum;
      }
    }, 0);
    if (categoryValues?.tax) {
      amount = parseInt(amount * (1 + categoryValues?.tax / 100));
      recurring_amount = parseInt(
        recurring_amount * (1 + categoryValues?.tax / 100)
      );
    }
    formHandleChange("amount", amount || 0);
    formHandleChange("recurring_amount", recurring_amount || 0);
  }, [criteriaValues, categoryValues?.tax]);
  return (
    <Box
      sx={{
        maxHeight: { xs: "none", md: "80vh" },
        height: { xs: "auto", md: "80vh" },
        overflow: { xs: "visible", md: "hidden" },
      }}
    >
      <Box
        sx={{
          height: { xs: "auto", md: "92%" },
          borderRadius: 4,
          boxSizing: "border-box",
          position: "relative",
          pointerEvents: !addNewCategory ? "none" : "auto",
          backgroundColor: !addNewCategory ? "#f0f0f0" : "white",
          pb: { xs: 2, md: 0 },
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            maxHeight: { xs: "none", md: "85%" },
            overflowY: { xs: "visible", md: "auto" },
            opacity: !addNewCategory ? "0.6" : "1",
            filter: !addNewCategory ? "blur(5px)" : "none",
            transition: "filter 0.3s ease",
            pointerEvents: !addNewCategory ? "none" : "auto",
            cursor: !addNewCategory ? "not-allowed" : "auto",
            borderRadius: 10,
          }}
        >
          <Box id="tour-invoice-items-grid" sx={{ mb: 2 }}>
            <FullFeaturedCrudGrid
              hideFooterPagination={true}
              initialRows={criteriaValues}
              delFun={readOnly ? undefined : setCriteriaValues}
              columns={columns(!readOnly)}
              label="invoiceitem"
              rowUpdateFun={rowUpdateFun}
              handleSelection={() => {}}
              refetch={categoryListRefetch}
              permission={!readOnly && permission}
              autoHeight={true}
              disabled={!addNewCategory || readOnly}
              isLocalTable={true}
              hideEdit={readOnly}
            ></FullFeaturedCrudGrid>
          </Box>
          <Box
            id="tour-invoice-details-fields"
            sx={{
              padding: { xs: "2px 16px", sm: "2px 40px" },
            }}
          >
            {categoryData?.invoiceNumber && (
              <Box sx={{ mb: 1, fontWeight: 600 }}>
                Invoice #: {categoryData.invoiceNumber}
              </Box>
            )}
            {categoryData?.gatewayStatus && (
              <Box sx={{ mb: 1, color: categoryData.gatewayStatus === "failed" ? "#d32f2f" : "inherit" }}>
                Gateway: {categoryData.gatewayStatus}
                {categoryData.gatewayMessage ? ` — ${categoryData.gatewayMessage}` : ""}
              </Box>
            )}
            <StyledTypography></StyledTypography>
            <CustomSelectField
              label="Invoiced to Company"
              style={{ width: "100%" }}
              name="companyID"
              accept="companyID"
              inputValues={fullCompanyList || []}
              value={categoryValues?.companyID}
              onChange={(e) => formHandleChange(e.target.name, e.target.value)}
              disabled={readOnly || !permission}
              isViewMode={readOnly || !permission}
            />
            <StyledTypography></StyledTypography>
            <CustomTextField
              label="Tax %"
              name="tax"
              fieldType="decimal"
              value={categoryValues?.tax}
              onChange={(e) => formHandleChange(e.target.name, e.target.value)}
              disabled={readOnly}
              isViewMode={readOnly}
            />
            <StyledTypography></StyledTypography>
            <CustomDatePicker
              label="Invoice Date"
              name="invoicedate"
              value={categoryValues?.invoicedate ? moment(categoryValues?.invoicedate) : null}
              maxDate={categoryValues?.duedate ? moment(categoryValues?.duedate) : undefined}
              onChange={(value) => formHandleChange("invoicedate", value)}
              disabled={readOnly}
              isViewMode={readOnly}
            />
            <StyledTypography></StyledTypography>
            <CustomDatePicker
              label="Invoice Due Date"
              name="duedate"
              value={categoryValues?.duedate ? moment(categoryValues?.duedate) : null}
              minDate={categoryValues?.invoicedate ? moment(categoryValues?.invoicedate) : undefined}
              onChange={(value) => formHandleChange("duedate", value)}
              disabled={readOnly}
              isViewMode={readOnly}
            />
            <div
              style={{
                justifyContent: "space-between",
                display: "flex",
                margin: "15px 0",
                fontWeight: 600,
              }}
            >
              Total : <span>{formatUsd(categoryValues?.amount)}</span>
            </div>
          </Box>
        </Box>
        <Box
          id="tour-invoice-form-actions"
          sx={{
            display: "flex",
            paddingRight: { xs: 0, sm: "40px" },
            justifyContent: { xs: "center", sm: "flex-end" },
            opacity: !addNewCategory ? "0.6" : "1",
            filter: !addNewCategory ? "blur(5px)" : "none",
            transition: "filter 0.3s ease",
            pointerEvents: !addNewCategory ? "none" : "auto",
            cursor: !addNewCategory ? "not-allowed" : "auto",
            mt: { xs: 2, md: 0 }
          }}
        >
          {categoryData?.status === "unpaid" && (
            <Button
              variant="contained"
              onClick={() => setOpenPayment(true)}
              disabled={payInvoiceMutation.isPending}
              style={{ marginRight: 20 }}
            >
              {payInvoiceMutation.isPending ? "Paying..." : "Pay"}
            </Button>
          )}
          {permission && !readOnly && (
            <Button
              variant="contained"
              style={{ marginRight: 20 }}
              onClick={sumbitCompanyForm}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isSaving ? "Saving..." : (categoryData?.invoiceID ? "Update" : "Save")}
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => {
              setCategoryValues(invoiceFormInitVal);
              setCriteriaValues([]);
              setCategoryData({});
              setAddNewCategory(false);
              setSelectedCategories([]);
              setCompanyForm([]);
            }}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </Box>
        <Modal open={openPayment} onClose={() => setOpenPayment(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
            }}
          >
            <h2 id="payment-modal">Payment</h2>

            <PaymentForm
              invoiceID={categoryData?.invoiceID}
              isSubmitting={payInvoiceMutation.isPending}
              onSubmit={(formDataOrArg) => {
                const invoiceID = categoryData?.invoiceID;

                // support both formats:
                // 1) PaymentForm returns { invoiceID, formData }
                // 2) PaymentForm returns only formData
                const isWrapped =
                  formDataOrArg &&
                  typeof formDataOrArg === "object" &&
                  "invoiceID" in formDataOrArg &&
                  "formData" in formDataOrArg;

                if (isWrapped) {
                  payInvoiceMutation.mutate(formDataOrArg);
                } else {
                  payInvoiceMutation.mutate({
                    invoiceID,
                    formData: formDataOrArg,
                  });
                }
              }}
            />
          </Box>
        </Modal>
      </Box>
    </Box>
  );
};
